import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCheck, FaTimes } from 'react-icons/fa';
import './Pricing.css';

const Pricing = () => {
  const { t } = useTranslation();
  const [billingCycle, setBillingCycle] = useState('yearly'); // 'monthly' or 'yearly'

  const plans = [
    {
      name: 'Free',
      price: { monthly: 0, yearly: 0 },
      description: 'Perfect for getting started',
      features: [
        { text: 'Up to 3 properties', included: true },
        { text: 'Basic document storage (50MB)', included: true },
        { text: 'Property photos & details', included: true },
        { text: 'Tax reminders', included: true },
        { text: 'Mobile app access', included: true },
        { text: 'GPS boundary capture', included: false },
        { text: 'Interactive maps', included: false },
        { text: 'AI-Watchman satellite monitoring', included: false, highlight: true },
        { text: 'Family tree', included: false },
        { text: 'Video guides', included: false },
        { text: 'Priority support', included: false }
      ],
      popular: false,
      cta: 'Get Started'
    },
    {
      name: 'Premium',
      price: { monthly: 9.99, yearly: 99 },
      description: 'Most popular - Includes AI-Watchman',
      features: [
        { text: 'Unlimited properties', included: true },
        { text: 'Unlimited document storage', included: true },
        { text: 'Property photos & details', included: true },
        { text: 'Tax reminders & payment history', included: true },
        { text: 'Mobile app access', included: true },
        { text: 'GPS boundary capture', included: true },
        { text: 'Interactive maps with satellite view', included: true },
        { text: '🤖 AI-Watchman: 24/7 satellite monitoring', included: true, highlight: true },
        { text: '🚨 Instant change detection alerts', included: true, highlight: true },
        { text: 'Satellite images every 15 days', included: true, highlight: true },
        { text: 'Family tree & heritage tracking', included: true },
        { text: 'Video guides (all languages)', included: true },
        { text: 'Priority email support', included: true }
      ],
      popular: true,
      cta: 'Start Premium'
    },
    {
      name: 'Business',
      price: { monthly: 29.99, yearly: 299 },
      description: 'For professionals - Advanced AI features',
      features: [
        { text: 'Everything in Premium', included: true },
        { text: '🤖 AI-Watchman for unlimited properties', included: true, highlight: true },
        { text: 'Priority AI processing & alerts', included: true, highlight: true },
        { text: 'Historical satellite data access', included: true, highlight: true },
        { text: 'Team collaboration (up to 10 users)', included: true },
        { text: 'Advanced analytics & reports', included: true },
        { text: 'Custom branding', included: true },
        { text: 'API access', included: true },
        { text: 'Bulk property import', included: true },
        { text: 'White-label option', included: true },
        { text: 'Dedicated account manager', included: true },
        { text: '24/7 priority support', included: true },
        { text: 'Training & onboarding', included: true }
      ],
      popular: false,
      cta: 'Contact Sales'
    }
  ];

  return (
    <div className="pricing-page">
      {/* Hero Section */}
      <section className="pricing-hero" data-aos="fade-up">
        <div className="container">
          <h1>{t('nav.pricing')}</h1>
          <p className="pricing-subtitle">Choose the perfect plan for your needs</p>
          
          {/* Billing Toggle */}
          <div className="billing-toggle">
            <button 
              className={billingCycle === 'monthly' ? 'active' : ''}
              onClick={() => setBillingCycle('monthly')}
            >
              Monthly
            </button>
            <button 
              className={billingCycle === 'yearly' ? 'active' : ''}
              onClick={() => setBillingCycle('yearly')}
            >
              Yearly <span className="save-badge">Save 17%</span>
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pricing-cards">
        <div className="container">
          <div className="pricing-grid">
            {plans.map((plan, index) => (
              <div 
                key={index} 
                className={`pricing-card ${plan.popular ? 'popular' : ''}`}
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                {plan.popular && <div className="popular-badge">Most Popular</div>}
                
                <div className="card-header">
                  <h3>{plan.name}</h3>
                  <p className="plan-desc">{plan.description}</p>
                  <div className="price">
                    <span className="currency">$</span>
                    <span className="amount">
                      {billingCycle === 'monthly' ? plan.price.monthly : plan.price.yearly}
                    </span>
                    <span className="period">
                      /{billingCycle === 'monthly' ? 'month' : 'year'}
                    </span>
                  </div>
                  {billingCycle === 'yearly' && plan.price.yearly > 0 && (
                    <p className="yearly-note">
                      ${(plan.price.yearly / 12).toFixed(2)}/month billed annually
                    </p>
                  )}
                </div>

                <ul className="features-list">
                  {plan.features.map((feature, i) => (
                    <li key={i} className={`${feature.included ? 'included' : 'not-included'} ${feature.highlight ? 'highlight-feature' : ''}`}>
                      {feature.included ? (
                        <FaCheck className="icon-check" />
                      ) : (
                        <FaTimes className="icon-times" />
                      )}
                      <span>{feature.text}</span>
                    </li>
                  ))}
                </ul>

                <button className={`btn ${plan.popular ? 'btn-primary' : 'btn-outline'} btn-block`}>
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="pricing-faq" data-aos="fade-up">
        <div className="container">
          <div className="section-header">
            <h2>Frequently Asked Questions</h2>
            <p>Everything you need to know about our pricing</p>
          </div>
          
          <div className="faq-grid">
            <div className="faq-item">
              <h3>Can I change plans later?</h3>
              <p>Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately.</p>
            </div>
            <div className="faq-item">
              <h3>Is there a free trial?</h3>
              <p>The Free plan is available forever. Premium and Business plans offer a 14-day money-back guarantee.</p>
            </div>
            <div className="faq-item">
              <h3>What payment methods do you accept?</h3>
              <p>We accept all major credit cards, debit cards, and digital payment methods.</p>
            </div>
            <div className="faq-item">
              <h3>Can I cancel anytime?</h3>
              <p>Absolutely. Cancel anytime from your account settings. No questions asked.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section" data-aos="fade-up">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Get Started?</h2>
            <p>Join thousands of property owners managing their land effortlessly</p>
            <button className="btn btn-primary btn-lg">
              Start Free Trial
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
